import {effect, Injectable, signal, WritableSignal} from '@angular/core';
import {UserPrefsService} from './user-prefs.service';

export type ThemePreference = 'light' | 'dark' | 'system';

const THEME_KEY = 'theme';
const DARK_CLASS = 'dark-theme';

@Injectable({
    providedIn: 'root'
})
export class ThemeService {

    readonly preference: WritableSignal<ThemePreference>;
    private systemDarkQuery = window.matchMedia('(prefers-color-scheme: dark)');

    constructor(private userPrefs: UserPrefsService) {
        const stored = this.userPrefs.get<ThemePreference>(THEME_KEY)() ?? 'light';
        this.preference = signal(stored);
        this.applyTheme(stored);

        // React to pref signal changes (e.g. when a different user's prefs are loaded after login)
        const themeSignal = this.userPrefs.get<ThemePreference>(THEME_KEY);
        effect(() => {
            const pref = themeSignal() ?? 'light';
            this.preference.set(pref);
            this.applyTheme(pref);
        });

        this.systemDarkQuery.addEventListener('change', () => {
            if (this.preference() === 'system')
                this.applyTheme('system');
        });
    }

    setTheme(pref: ThemePreference) {
        this.preference.set(pref);
        this.userPrefs.set(THEME_KEY, pref);
        this.applyTheme(pref);
    }

    private applyTheme(pref: ThemePreference) {
        const isDark = pref === 'dark' || (pref === 'system' && this.systemDarkQuery.matches);
        const html = document.documentElement;

        if (isDark)
            html.classList.add(DARK_CLASS);
        else
            html.classList.remove(DARK_CLASS);

        // Update PWA theme-color meta tag
        const meta = document.querySelector('meta[name="theme-color"]');
        if (meta)
            meta.setAttribute('content', isDark ? '#1e1e1e' : '#1976d2');
    }
}
